import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ProductScreenshot from '../products/ProductScreenshot';
import { useMarket } from '../../context/MarketContext';
import { ProductColor } from '../../data/products';
import { productTheme } from '../../utils/productTheme';

type CalloutTheme = 'primary' | 'accent' | 'emerald' | 'violet' | 'rx';

type Callout = {
  benefit: string;
  detail: string;
  theme: CalloutTheme;
  href: string;
  aiIcon?: boolean;
};

type ShowcaseSlide = {
  id: string;
  productName: string;
  color: ProductColor;
  imageSrc: string;
  imageAlt: string;
  urlLabel: string;
  showAiBadge?: boolean;
  callouts: Callout[];
};

const themeStyles: Record<
  CalloutTheme,
  { card: string; iconBg: string; icon: string; detail: string }
> = {
  primary: {
    card: 'border-primary-300/70 dark:border-primary-500/45 shadow-primary-500/15 hover:border-primary-500/90 dark:hover:border-primary-400/60',
    iconBg: 'bg-primary-100 dark:bg-primary-500/20',
    icon: 'text-primary-600 dark:text-primary-400',
    detail: 'text-slate-600 dark:text-dark-400',
  },
  accent: {
    card: 'border-accent-300/70 dark:border-accent-500/45 shadow-accent-500/15 hover:border-accent-500/90 dark:hover:border-accent-400/60',
    iconBg: 'bg-accent-100 dark:bg-accent-500/20',
    icon: 'text-accent-600 dark:text-accent-400',
    detail: 'text-slate-600 dark:text-dark-400',
  },
  emerald: {
    card: 'border-emerald-300/70 dark:border-emerald-500/45 shadow-emerald-500/15 hover:border-emerald-500/90 dark:hover:border-emerald-400/60',
    iconBg: 'bg-emerald-100 dark:bg-emerald-500/20',
    icon: 'text-emerald-600 dark:text-emerald-400',
    detail: 'text-slate-600 dark:text-dark-400',
  },
  violet: {
    card: 'border-violet-300/70 dark:border-violet-500/45 shadow-violet-500/15 hover:border-violet-500/90 dark:hover:border-violet-400/60',
    iconBg: 'bg-violet-100 dark:bg-violet-500/20',
    icon: 'text-violet-600 dark:text-violet-400',
    detail: 'text-slate-600 dark:text-dark-400',
  },
  rx: {
    card: 'border-rx-300/70 dark:border-rx-500/45 shadow-rx-500/15 hover:border-rx-500/90 dark:hover:border-rx-400/60',
    iconBg: 'bg-rx-100 dark:bg-rx-500/20',
    icon: 'text-rx-600 dark:text-rx-400',
    detail: 'text-slate-600 dark:text-dark-400',
  },
};

const flowCallouts: Callout[] = [
  {
    benefit: 'Less paperwork',
    detail: 'Replace spreadsheets, paper travelers, and duplicate data entry',
    theme: 'primary',
    href: '/products/cimmpleflow',
  },
  {
    benefit: 'Live job visibility',
    detail: 'Track every job in real time from quote to shipment',
    theme: 'emerald',
    href: '/products/cimmpleflow',
  },
  {
    benefit: 'Faster quoting',
    detail: 'Respond to customers quicker with connected costs and VendorConnect™',
    theme: 'accent',
    href: '/products/cimmpleflow',
  },
  {
    benefit: 'AI-assisted work',
    detail: 'AI extracts invoice data from PDFs & email',
    theme: 'violet',
    href: '/products/cimmpleflow',
    aiIcon: true,
  },
];

const payCallouts: Callout[] = [
  {
    benefit: 'Payroll in minutes',
    detail: 'Automate salary calculations, overtime, and deductions',
    theme: 'accent',
    href: '/products/cimmlepay',
  },
  {
    benefit: 'Stay compliant',
    detail: 'Tax calculations, withholdings, and filings stay current',
    theme: 'primary',
    href: '/products/cimmlepay',
  },
  {
    benefit: 'Employee self-service',
    detail: 'Pay stubs, tax docs, and updates without HR ticket queues',
    theme: 'emerald',
    href: '/products/cimmlepay',
  },
  {
    benefit: 'Accurate attendance',
    detail: 'Face-recognition clock-in for fraud-resistant time tracking',
    theme: 'violet',
    href: '/products/cimmlepay',
  },
];

const rxCallouts: Callout[] = [
  {
    benefit: 'Faster counter billing',
    detail: 'GST-ready POS billing with cash, card, UPI, and credit',
    theme: 'rx',
    href: '/products/cimmplerx',
  },
  {
    benefit: 'Batch & expiry control',
    detail: 'Track medicines by batch and catch expiring stock early',
    theme: 'emerald',
    href: '/products/cimmplerx',
  },
  {
    benefit: 'Smarter purchasing',
    detail: 'Suggested orders from stock levels and sales velocity',
    theme: 'primary',
    href: '/products/cimmplerx',
  },
  {
    benefit: 'GST-ready books',
    detail: 'Accounting, GST reports, and shop finances in one place',
    theme: 'rx',
    href: '/products/cimmplerx',
  },
];

const flowSlide: ShowcaseSlide = {
  id: 'cimmpleflow',
  productName: 'CimmpleFlow',
  color: 'primary',
  imageSrc: '/images/platform/cimmpleflow-home.png',
  imageAlt: 'CimmpleFlow dashboard showing orders, jobs, revenue, and operational analytics',
  urlLabel: 'app.cimmple.com',
  showAiBadge: true,
  callouts: flowCallouts,
};

const paySlide: ShowcaseSlide = {
  id: 'cimmlepay',
  productName: 'CimmplePay',
  color: 'accent',
  imageSrc: '/images/platform/cimmplepay-home.png',
  imageAlt: 'CimmplePay payroll management dashboard',
  urlLabel: 'payroll.cimmple.net',
  callouts: payCallouts,
};

const rxSlide: ShowcaseSlide = {
  id: 'cimmplerx',
  productName: 'CimmpleRx',
  color: 'rx',
  imageSrc: '/images/platform/cimmplerx-home.png',
  imageAlt: 'CimmpleRx pharmacy dashboard showing billing, inventory, and operational overview',
  urlLabel: 'pharma.cimmple.net',
  callouts: rxCallouts,
};

const AUTOPLAY_MS = 7000;

const CalloutIcon: React.FC<{ callout: Callout; className: string }> = ({ callout, className }) => {
  if (callout.aiIcon) {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
    );
  }
  if (callout.theme === 'emerald') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    );
  }
  if (callout.theme === 'rx') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3h6v4h4v6h-4v4H9v-4H5V7h4V3z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 21h12" />
      </svg>
    );
  }
  if (callout.theme === 'accent') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    );
  }
  if (callout.theme === 'violet') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    );
  }
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
};

const CalloutCard: React.FC<{ callout: Callout }> = ({ callout }) => {
  const theme = themeStyles[callout.theme];

  return (
    <Link
      to={callout.href}
      className={`flex h-full min-h-[132px] w-[248px] sm:w-[256px] lg:w-full flex-col rounded-xl border bg-white/95 dark:bg-dark-900/95 backdrop-blur-sm px-4 py-4 shadow-lg text-left transition-colors duration-300 ${theme.card}`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mb-3 ${theme.iconBg}`}>
        <CalloutIcon callout={callout} className={`w-4 h-4 ${theme.icon}`} />
      </div>

      <h3
        className="text-sm font-semibold text-slate-900 dark:text-white whitespace-nowrap mb-1.5"
        title={callout.benefit}
      >
        {callout.benefit}
      </h3>

      <p className={`text-xs leading-relaxed flex-1 ${theme.detail}`}>{callout.detail}</p>
    </Link>
  );
};

const AiBadge: React.FC = () => (
  <div
    className="absolute top-0 left-[calc(3.25rem+1in)] sm:left-[calc(3.5rem+1in)] z-20 flex flex-col items-center pointer-events-none"
    aria-hidden="true"
  >
    <div className="relative z-10 -mb-px">
      <div className="w-4 h-4 rounded-full border-2 border-violet-300/90 dark:border-violet-400 bg-white dark:bg-dark-800 shadow-sm" />
      <div className="absolute inset-[5px] rounded-full bg-gradient-to-br from-violet-400/40 to-primary-500/30" />
    </div>

    <div
      className="relative w-[3.75rem] sm:w-16 pt-2.5 pb-5 px-2 text-center shadow-xl shadow-violet-900/25 ring-1 ring-inset ring-white/25"
      style={{
        background: 'linear-gradient(165deg, #8b5cf6 0%, #7c3aed 38%, #4f46e5 100%)',
        clipPath: 'polygon(0 0, 100% 0, 100% 88%, 78% 74%, 50% 100%, 22% 74%, 0 88%)',
      }}
    >
      <div
        className="absolute inset-x-1 top-0 h-1/2 rounded-b-sm bg-gradient-to-b from-white/30 to-transparent pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute left-1 top-2 bottom-6 w-px bg-white/20 pointer-events-none"
        aria-hidden="true"
      />
      <p className="relative font-sans">
        <span className="block text-[13px] sm:text-sm font-extrabold text-white leading-none tracking-normal">
          AI
        </span>
        <span className="block mt-1 text-[10px] sm:text-[11px] font-medium italic lowercase text-violet-100 leading-none tracking-normal">
          inside
        </span>
      </p>
    </div>
  </div>
);

const PlatformVisual: React.FC = () => {
  const { market } = useMarket();
  const slides = market === 'IN' ? [flowSlide, rxSlide] : [flowSlide, paySlide];
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    setActiveIndex(0);
  }, [market]);

  const goTo = useCallback(
    (index: number) => {
      setActiveIndex(((index % slides.length) + slides.length) % slides.length);
    },
    [slides.length]
  );

  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  useEffect(() => {
    if (isPaused || slides.length < 2) return undefined;
    const id = window.setInterval(goNext, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [goNext, isPaused, slides.length]);

  const slide = slides[activeIndex] ?? slides[0];
  const theme = productTheme(slide.color);

  return (
    <div
      className="mt-16 lg:mt-20 w-full max-w-5xl mx-auto animate-slide-up animate-delay-300"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setIsPaused(false);
        }
      }}
    >
      <div
        className="inline-flex items-center p-1.5 rounded-2xl bg-slate-100 dark:bg-dark-800/50 border border-slate-200 dark:border-dark-700 mb-6"
        role="tablist"
        aria-label="Product showcase"
      >
        {slides.map((item, index) => {
          const isActive = index === activeIndex;
          const itemTheme = productTheme(item.color);

          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`showcase-panel-${item.id}`}
              id={`showcase-tab-${item.id}`}
              onClick={() => goTo(index)}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                isActive
                  ? itemTheme.tabActive
                  : 'text-slate-600 dark:text-dark-300 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              {item.productName}
            </button>
          );
        })}
      </div>

      <div
        id={`showcase-panel-${slide.id}`}
        role="tabpanel"
        aria-labelledby={`showcase-tab-${slide.id}`}
        className="relative"
      >
        <div
          className={`absolute -inset-4 sm:-inset-8 rounded-[2rem] ${theme.glow} blur-2xl pointer-events-none transition-colors duration-500`}
          aria-hidden="true"
        />

        <div className="relative">
          <div key={slide.id} className="animate-fade-in">
            <ProductScreenshot
              imageSrc={slide.imageSrc}
              imageAlt={slide.imageAlt}
              productName={slide.productName}
              color={slide.color}
              urlLabel={slide.urlLabel}
              loading={activeIndex === 0 ? 'eager' : 'lazy'}
            />
            {slide.showAiBadge && <AiBadge />}
          </div>

          {slides.length > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous product"
                className={`absolute -left-1 sm:-left-5 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-2xl text-white shadow-xl ring-2 ring-white/80 dark:ring-dark-800/80 transition-transform duration-300 hover:scale-110 ${theme.arrow}`}
              >
                <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next product"
                className={`absolute -right-1 sm:-right-5 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-2xl text-white shadow-xl ring-2 ring-white/80 dark:ring-dark-800/80 transition-transform duration-300 hover:scale-110 ${theme.arrow}`}
              >
                <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mt-6 -mx-4 px-4 sm:mx-0 sm:px-0" key={`${slide.id}-callouts`}>
        <div className="flex flex-row flex-nowrap lg:grid lg:grid-cols-4 gap-3 sm:gap-4 items-stretch justify-start lg:justify-center overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 snap-x snap-mandatory lg:snap-none animate-fade-in">
          {slide.callouts.map((callout) => (
            <div key={`${slide.id}-${callout.benefit}`} className="snap-center flex lg:min-w-0">
              <CalloutCard callout={callout} />
            </div>
          ))}
        </div>
      </div>

      {slides.length > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2" aria-label="Showcase slides">
          {slides.map((item, index) => {
            const itemTheme = productTheme(item.color);
            return (
              <button
                key={item.id}
                type="button"
                aria-label={`Show ${item.productName}`}
                aria-current={index === activeIndex ? 'true' : undefined}
                onClick={() => goTo(index)}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  index === activeIndex
                    ? `w-8 ${itemTheme.dot}`
                    : 'w-2.5 bg-slate-300 dark:bg-dark-600 hover:bg-slate-400 dark:hover:bg-dark-500'
                }`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlatformVisual;
